import { WorkspaceRole, WorkspaceSeatType, WorkspaceUserProfile } from 'models/user'
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'

import { embedSession } from 'utils/embedSession'
import { logger } from 'utils/logging'
import { useAuth0 } from 'utils/auth'
import { useDelayedFlag } from './useDelayedFlag'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * How long a deep link waits for the workspace selection before giving up on it. UserDataContext has
 * branches that never resolve (see usePricingRedirectTarget), and a permanent spinner is worse than
 * landing on a page with the tab's current selection.
 */
export const WORKSPACE_DEEP_LINK_TIMEOUT_MS = 5000

/**
 * How a workspace deep link resolved. Meaningful once `isResolving` is false.
 *
 * - `switched` — the tab was switched into the linked workspace
 * - `already_selected` — the linked workspace was already the tab's selection
 * - `not_member` — the user is not an active member of it (unknown id, left, or a still-pending invitation)
 * - `embed_pinned` — an embed session pins the workspace, so nothing was switched
 * - `timeout` — the workspace selection never resolved within {@link WORKSPACE_DEEP_LINK_TIMEOUT_MS}
 * - `none` — no workspace id was linked
 */
export type WorkspaceDeepLinkOutcome = 'switched' | 'already_selected' | 'not_member' | 'embed_pinned' | 'timeout' | 'none'

export interface WorkspaceDeepLinkState {
  /** True while the selection is loading or the switch into the linked workspace has not landed yet. */
  isResolving: boolean
  /** The linked workspace, once resolved, when the user is an active member and no embed pins the tab. */
  targetWorkspace: WorkspaceUserProfile | undefined
  outcome: WorkspaceDeepLinkOutcome
  /**
   * Whether a Members deep link should keep the user on Members: admins without a sponsored seat. Stricter
   * than WorkspacePageController, which bounces a sponsored seat off workspace settings entirely but also
   * shows Members to a non-restricted member: a deep link deliberately lands every non-admin on profile
   * settings instead. False whenever there is no target workspace.
   */
  canSeeMembers: boolean
}

/**
 * Switches the tab into the workspace a link points at — for this tab only, like landing on a property
 * link, so no lasting preference is recorded.
 *
 * It waits for UserDataContext to resolve the selection first (switching earlier would race it), and gives
 * up after {@link WORKSPACE_DEEP_LINK_TIMEOUT_MS} with a `logger.warn`. The switch fires at most once per
 * mount, so a `userMe` refetch handing out fresh membership objects cannot re-fire it. Only an active
 * membership is switched to, and never inside an embed session, whose host pins the workspace and would
 * undo the switch.
 *
 * Callers should hold their page back while `isResolving`: the settings pages gate on the selected
 * workspace's role, so rendering them before the switch lands checks the wrong workspace.
 *
 * @param targetWorkspaceId - the linked workspace id; `undefined` resolves straight away to `none`
 */
export function useWorkspaceDeepLink(targetWorkspaceId: string | undefined): WorkspaceDeepLinkState {
  const { isAuthenticated, roles } = useAuth0()
  const { currentUserWorkspace, setCurrentUserWorkspace, getWorkspaceItemById, isWorkspaceResolved } = useUserData()
  const embedWorkspaceId = useSyncExternalStore(embedSession.subscribe, embedSession.getWorkspaceId)

  const hasTarget = targetWorkspaceId !== undefined
  // Only clients have workspaces; for anyone else the selection never resolves, so there is nothing to wait for.
  const awaitsWorkspace = hasTarget && isAuthenticated && roles.isClient && !isWorkspaceResolved
  const resolutionTimedOut = useDelayedFlag(awaitsWorkspace, WORKSPACE_DEEP_LINK_TIMEOUT_MS)

  // useDelayedFlag drops back to false as soon as the selection resolves, so latch the timeout: a late
  // resolution must not reopen a link the caller has already given up on.
  const [hasTimedOut, setHasTimedOut] = useState(false)
  useEffect(() => {
    if (!resolutionTimedOut || hasTimedOut) return
    setHasTimedOut(true)

    logger.warn('Workspace deep link timed out waiting for the workspace selection', undefined, {
      context: {
        component: 'useWorkspaceDeepLink',
        action: 'workspaceResolutionTimeout',
        metadata: { hasWorkspace: !!currentUserWorkspace, linkedWorkspaceId: targetWorkspaceId },
      },
    })
  }, [resolutionTimedOut, hasTimedOut, currentUserWorkspace, targetWorkspaceId])

  const timedOut = hasTimedOut || resolutionTimedOut
  const isLoading = awaitsWorkspace && !timedOut

  const targetWorkspace = useMemo(
    () => (targetWorkspaceId === undefined || isLoading || timedOut || embedWorkspaceId) ? undefined : getWorkspaceItemById(targetWorkspaceId),
    [isLoading, timedOut, embedWorkspaceId, getWorkspaceItemById, targetWorkspaceId]
  )

  // Switch in an effect and report resolved only on the render after it lands. The ref keeps a later
  // re-resolution from re-firing it; the state only records that a switch happened, for the outcome.
  const hasSwitchedRef = useRef(false)
  const [didSwitch, setDidSwitch] = useState(false)
  useEffect(() => {
    if (!targetWorkspace || hasSwitchedRef.current) return
    hasSwitchedRef.current = true

    if (currentUserWorkspace?.id === targetWorkspace.id) return
    sessionStorage.setItem('workspaceId', targetWorkspace.id)
    setCurrentUserWorkspace(targetWorkspace)
    setDidSwitch(true)
    // currentUserWorkspace is read only to skip a no-op switch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetWorkspace, setCurrentUserWorkspace])

  const isSwitchPending = !!targetWorkspace && currentUserWorkspace?.id !== targetWorkspace.id

  const canSeeMembers = targetWorkspace?.role === WorkspaceRole.ADMIN && targetWorkspace.seatType !== WorkspaceSeatType.SPONSORED

  const outcome: WorkspaceDeepLinkOutcome = useMemo(() => {
    if (!hasTarget) return 'none'
    if (timedOut) return 'timeout'
    if (embedWorkspaceId) return 'embed_pinned'
    if (!targetWorkspace) return 'not_member'
    return didSwitch ? 'switched' : 'already_selected'
  }, [hasTarget, timedOut, embedWorkspaceId, targetWorkspace, didSwitch])

  return {
    isResolving: isLoading || isSwitchPending,
    targetWorkspace,
    outcome,
    canSeeMembers,
  }
}
